/* WHICH KIND OF OUTREACH IS ACTUALLY WORKING.
 *
 * Brian: "I want to make sure I have visibility on it so we can measure it and
 * manage it. If we see our team has higher close rate with video, I want it
 * pushed harder."
 *
 * ⚠️ THIS IS A COMPARISON, NOT A DASHBOARD OF TOTALS. A big "412 campaigns
 * staged" number is activity and tells a manager nothing to act on. The
 * question is which ANGLE earns meetings, so the angle is the row and the
 * totals only ever appear as a denominator.
 *
 * ⚠️ AND IT SHOWS THE GAPS THE SERVER DECLARES, ABOVE THE NUMBERS.
 * The payload carries a `gaps[]` — no outreach yet, campaigns staged before the
 * angle selector existed, and above all that CLOSE RATE IS NOT SOURCED. A
 * dashboard that prints a confident close rate nobody can trace is worse than
 * one that says the link is missing, because the confident one gets acted on.
 * Every rate here is either real or blank with a reason. [[silent-zero-pattern]]
 */
import { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import styles from './OutreachScoreboard.module.css'

const rate = (v) => (v == null ? null : `${v}%`)
const meetingBasis = (row) => `${row.matchedMeetingOpportunities ?? 'Unknown'} matching meeting opportunities from ${row.meetingDenominator ?? 'unknown'} opportunities with recorded ${row.meetingRateBasis === 'opportunities_with_recorded_approval' ? 'approval' : 'preparation'}.${row.unmatchedMeetings ? ` ${row.unmatchedMeetings} other meeting opportunities lack this matching denominator.` : ''}`

export default function OutreachScoreboard() {
  const [s, setS] = useState({ status: 'loading' })

  useEffect(() => {
    let alive = true
    api.outreachEffectiveness()
      .then((r) => { if (alive) setS(r?.ok ? { status: 'done', r } : { status: 'error', detail: r?.note || r?.detail || r?.error }) })
      .catch((e) => { if (alive) setS({ status: 'error', detail: e?.message || String(e) }) })
    return () => { alive = false }
  }, [])

  if (s.status === 'loading') return <div className={styles.wrap}><p className={styles.soft}>Reading the outreach log…</p></div>
  /* ⚠️ A FAILED READ IS NOT AN EMPTY SCOREBOARD, and must not render as zeros —
   * that would be a claim about the team rather than about the connection. */
  if (s.status === 'error') {
    return (
      <div className={styles.wrap}>
        <div className={styles.err}>
          <b>Could not read the outreach log.</b>
          <span>{s.detail}</span>
          <em>This is not a zero — no rates are shown because none could be sourced.</em>
        </div>
      </div>
    )
  }

  const { angles = [], reps = [], totals = {}, gaps = [], since, scope, portfolioSource } = s.r
  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <b>Outreach by type</b>
        <span className={styles.soft}>
          since {String(since).slice(0, 10)} · {totals.staged || 0} unique opportunities prepared · {totals.approved || 0} with approval · {totals.meetings || 0} with a recorded meeting
        </span>
      </div>

      {scope && <p className={styles.soft}>{scope.mode === 'all' ? 'Company portfolio' : scope.mode === 'unassigned' ? 'Unassigned portfolio' : scope.rep || 'Your assigned portfolio'}{portfolioSource?.asOf ? ` · portfolio snapshot ${new Date(portfolioSource.asOf).toLocaleString()}` : ''}{portfolioSource?.stale ? ' · stale snapshot' : ''}</p>}

      {/* The gaps come FIRST and in the server's own words. */}
      {gaps.length > 0 && (
        <ul className={styles.gaps}>
          {gaps.map((g) => <li key={g}>⚠️ {g}</li>)}
        </ul>
      )}

      <p className={styles.lead}>Each opportunity counts once within an outreach type. Meeting progress uses matching recorded preparation or approval. These figures do not establish conversion from delivered messages.</p>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Outreach type</th><th>Segment</th>
            <th>Staged</th><th>Approved</th><th>Meetings</th>
            <th>Approval progress</th><th>Recorded meeting progress</th><th>Close rate</th>
          </tr>
        </thead>
        <tbody>
          {angles.map((a) => (
            /* Declared a readout: an angle with its staged / approved counts is
               a tally line, not a record to open. */
            <tr key={a.angle} data-zero={a.staged === 0 ? '1' : '0'} aria-disabled="true">
              <td><b>{a.label}</b></td>
              <td className={styles.soft}>{a.segment}</td>
              <td>{a.staged}</td>
              <td>{a.approved}</td>
              <td>{a.meetings}</td>
              <td>{rate(a.approvalRate) ?? <span className={styles.soft}>—</span>}</td>
              <td title={meetingBasis(a)}>{rate(a.meetingRate) ?? <span className={styles.soft}>—</span>}{a.meetingDenominator > 0 && <small className={styles.soft}> ({a.matchedMeetingOpportunities}/{a.meetingDenominator})</small>}</td>
              {/* Never a number. The server returns null and says why. */}
              <td className={styles.soft} title="Not sourced — no link from a deal back to the campaign that caused it.">
                not sourced
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {reps.length > 0 && (
        <>
          <div className={styles.head}><b>By recorded actor</b></div>
          <table className={styles.table}>
            <thead><tr><th>Recorded actor</th><th>Prepared</th><th>Approved</th><th>Meetings</th><th>Recorded meeting progress</th></tr></thead>
            <tbody>
              {reps.map((r) => (
                /* Declared a readout: a per-rep outreach tally, not a book to open.
               The manager rollup is where a rep row opens their work. */
            <tr key={r.rep} aria-disabled="true">
                  <td>{r.rep}</td><td>{r.staged}</td><td>{r.approved}</td><td>{r.meetings}</td>
                  <td title={meetingBasis(r)}>{rate(r.meetingRate) ?? <span className={styles.soft}>—</span>}{r.meetingDenominator > 0 && <small className={styles.soft}> ({r.matchedMeetingOpportunities}/{r.meetingDenominator})</small>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
